import React, { useState, useCallback, CSSProperties } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import { CheckCircle } from "@mui/icons-material";
import { Button, Text } from "$components";
import { ProductService } from "$services";

const GREY = "#CCC";
const GREY_LIGHT = "rgba(255, 255, 255, 0.4)";
const DEFAULT_REMOVE_HOVER_COLOR = "#A01919";
const GREY_DIM = "#686868";

const styles = {
  errorBox: {
    marginTop: 20,
    paddingTop: 10,
    borderTop: "1px solid #ddd",
    width: "100%",
    fontSize: 14,
    maxHeight: "var(--fourtyVh)",
    overflowY: "auto",
  } as CSSProperties,
  errorSpan: {
    color: "tomato",
    fontWeight: "bold",
  } as CSSProperties,
  zone: {
    alignItems: "center",
    border: `2px dashed ${GREY}`,
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    padding: 20,
    cursor: "pointer",
  } as CSSProperties,
  file: {
    background: "linear-gradient(to bottom, #EEE, #DDD)",
    borderRadius: 20,
    display: "flex",
    height: 120,
    width: 120,
    position: "relative",
    zIndex: 10,
    flexDirection: "column",
    justifyContent: "center",
  } as CSSProperties,
  info: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    paddingLeft: 10,
    paddingRight: 10,
  } as CSSProperties,
  size: {
    backgroundColor: GREY_LIGHT,
    borderRadius: 3,
    marginBottom: "0.5em",
    justifyContent: "center",
    display: "flex",
  } as CSSProperties,
  name: {
    backgroundColor: GREY_LIGHT,
    borderRadius: 3,
    fontSize: 12,
    marginBottom: "0.5em",
    wordBreak: "break-all",
    textAlign: "center",
  } as CSSProperties,
  removeBtn: {
    marginTop: 8,
    fontSize: 11,
    color: DEFAULT_REMOVE_HOVER_COLOR,
    cursor: "pointer",
    background: "none",
    border: "none",
  } as CSSProperties,
  zoneHover: {
    borderColor: GREY_DIM,
  } as CSSProperties,
};

function ReferenceCSVReader({
  onConfirmAddRefList,
  currentCompany,
  alreadySelectedProducts,
}: any) {
  const [errors, setErrors] = useState<any[]>([]);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>("");

  const processFile = useCallback(
    async (file: File) => {
      setErrors([]);
      setUploadedData([]);
      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = async () => {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const allRows: any[][] = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        });

        const headers = allRows[0] || [];
        const body = allRows
          .slice(1)
          .filter((row) => String(row[0]).trim() !== "");

        const productsReferences = body.map((line) => String(line[0]));
        const productList =
          await ProductService.getProductsByReferencesAndCompany(
            productsReferences,
            currentCompany
          );

        const productsDatas: any[] = [];
        const currentErrors: any[] = [];

        body.forEach((line: any, lineIndex: number) => {
          // Trim trailing empty cells to get effective column count
          let effectiveLength = line.length;
          while (
            effectiveLength > 0 &&
            (line[effectiveLength - 1] === "" ||
              line[effectiveLength - 1] === undefined)
          ) {
            effectiveLength--;
          }

          if (effectiveLength !== 2) {
            currentErrors.push({
              message:
                "Attention, le fichier que vous essayez d'importer n'est pas conforme (il semble comporter plus (ou moins) de 2 colonnes). Veuillez télécharger le template proposé afin d'ajuster vos données.",
              line: lineIndex,
              column: "",
            });
          }

          [line[0], line[1]].forEach((cell: any, columnIndex: number) => {
            if (cell === "" || cell === undefined) {
              currentErrors.push({
                message: `Champs "${
                  headers[columnIndex] ||
                  (columnIndex === 0 ? "Référence" : "Quantité")
                }" manquant`,
                line: lineIndex,
                column: columnIndex === 0 ? "A" : "B",
              });
            }
          });

          const productRef = String(line[0]);
          const foundProduct = productList.find(
            (p: any) => p.reference === productRef
          );

          if (productsDatas.find((pr: any) => pr.reference === productRef)) {
            currentErrors.push({
              message: `La référence "${productRef}" a déjà été utilisée dans le fichier, veuillez vérifier votre document.`,
              line: lineIndex,
              column: "A",
            });
          }

          if (
            alreadySelectedProducts.find(
              (ps: any) => ps.reference === productRef
            )
          ) {
            currentErrors.push({
              message: `La référence "${productRef}" a déjà été utilisée dans cette sous-commande.`,
              line: lineIndex,
              column: "A",
            });
          }

          if (!foundProduct) {
            currentErrors.push({
              message: `La référence "${productRef}" ne semble pas exister pour cette entreprise`,
              line: lineIndex,
              column: "A",
            });
          }

          productsDatas.push({
            reference: foundProduct?.reference,
            name: foundProduct?.designation,
            unit: foundProduct?.unit,
            quantity: line[1],
          });
        });

        setErrors(currentErrors);
        if (!currentErrors.length) {
          setUploadedData(productsDatas);
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [currentCompany, alreadySelectedProducts]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        processFile(acceptedFiles[0]);
      }
    },
    [processFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
  });

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileName("");
    setErrors([]);
    setUploadedData([]);
  };

  return (
    <>
      <div
        {...getRootProps()}
        style={{
          ...styles.zone,
          ...(isDragActive && styles.zoneHover),
        }}
      >
        <input {...getInputProps()} />

        {!errors.length && fileName && (
          <div style={styles.file}>
            <div style={styles.info}>
              <span style={styles.name}>{fileName}</span>
            </div>
            <button
              style={styles.removeBtn}
              onClick={handleReset}
              type="button"
            >
              Supprimer
            </button>
          </div>
        )}

        {errors.length > 0 && (
          <>
            <Text style={{ textAlign: "center" }}>
              Glissez un autre fichier XLSX ici <br />
              ou cliquez pour importer
            </Text>
            <div style={styles.errorBox}>
              {errors.map((err: any, errorIndex: number) => {
                const key = `error-${errorIndex}`;
                return (
                  <p key={key}>
                    <span style={styles.errorSpan}>
                      {err.column
                        ? `Cellule ${err.column}${err.line + 2} : `
                        : `Ligne ${err.line + 2} : `}
                    </span>
                    <br />
                    {err.message}
                  </p>
                );
              })}
            </div>
          </>
        )}

        {!fileName && (
          <Text>Glissez un fichier XLSX ici ou cliquez pour importer</Text>
        )}
      </div>

      {uploadedData?.length > 0 && (
        <div
          style={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 20,
          }}
        >
          <Text style={{ textAlign: "center", width: "100%" }}>
            {`Vous êtes sur le point d'importer ${uploadedData?.length}
            référence${uploadedData?.length > 1 ? "s" : ""}`}
          </Text>
          <Button
            style={{ marginTop: 20 }}
            onClick={() => onConfirmAddRefList(uploadedData)}
          >
            <CheckCircle style={{ marginRight: 8 }} />
            Confirmer les ajouts
          </Button>
        </div>
      )}
    </>
  );
}

export default ReferenceCSVReader;
